import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';

// Layouts & Pages
import MainLayout from './components/layout/MainLayout';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CoursesPage from './pages/CoursesPage';
import ClassTimetablePage from './pages/ClassTimetablePage';
import ExamTimetablePage from './pages/ExamTimetablePage';
import VersionPage from './pages/VersionPage';
import PublicTimetableView from './pages/PublicTimetableView';
import StudentsPage from './pages/StudentsPage';
import { RoomsPage, AcademicYearsPage, TimeSlotsPage } from './pages/ResourcePages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

// Protect routes helper
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>

            {/* Public Routes - Accessible by everyone */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* New Public Timetable View
               Placed outside ProtectedRoute so guests/students can view it
            */}
            <Route path="/timetable" element={<PublicTimetableView />} />
            <Route path="/version" element={<VersionPage />} />

            {/* Protected Routes - Admin Only */}
            <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/academic-years" element={<AcademicYearsPage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/rooms" element={<RoomsPage />} />
              <Route path="/time-slots" element={<TimeSlotsPage />} />
              <Route path="/class-timetable" element={<ClassTimetablePage />} />
              <Route path="/exam-timetable" element={<ExamTimetablePage />} />
              <Route path="/students" element={<StudentsPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </BrowserRouter>

        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#1e293b',
              color: '#f1f5f9',
              border: '1px solid #334155'
            },
            success: {
              iconTheme: { primary: '#6366f1', secondary: '#fff' }
            }
          }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}